import { DAY_NAMES } from '@/lib/constants';

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export type AbsencePrintStatus = 'unscheduled' | 'scheduled' | 'completed' | 'notNeeded';

export type AbsencePrintRow = {
  date: string; // 결석일 (YYYY-MM-DD)
  studentName: string;
  grade?: string | null;
  className?: string | null;
  reason: string;
  status: AbsencePrintStatus;
  statusText: string; // 보충 미정 / 보충예정 · 9/30 ...
  makeupDate?: string | null;
  makeupTime?: string | null;
  completedDate?: string | null;
};

export type AbsencePrintSummary = {
  total: number;
  scheduled: number;
  completed: number;
  unscheduled: number;
  notNeeded: number;
};

export type AbsencePrintOptions = {
  title: string;
  subtitle?: string;
  monthLabel: string;
  teacherName: string;
  summary: AbsencePrintSummary;
  rows: AbsencePrintRow[];
};

function weekdayLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return '';
  const date = new Date(y, m - 1, d);
  return DAY_NAMES[date.getDay()];
}

function shortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const [, m, d] = dateStr.split('-');
  if (!m || !d) return dateStr;
  return `${Number(m)}/${Number(d)}`;
}

// 보충 상태별 색상 배지 (인쇄 시에도 구분되게 배경색 사용)
const STATUS_BADGE: Record<AbsencePrintStatus, { bg: string; color: string }> = {
  unscheduled: { bg: '#fde4cf', color: '#9a3412' },
  scheduled: { bg: '#e2e8f0', color: '#334155' },
  completed: { bg: '#dcfce7', color: '#166534' },
  notNeeded: { bg: '#f1f5f9', color: '#64748b' },
};

// 선생님(또는 전체/담당 미지정)별 결석·보충 내역을 한 장으로 인쇄해요.
// 팝업이 막히는 환경에서도 동작하도록 숨김 iframe을 사용해요.
export function printAbsenceCalendar(options: AbsencePrintOptions): void {
  const { title, subtitle, monthLabel, teacherName, summary, rows } = options;

  const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));

  const summaryCells = [
    { label: '이번 달 결석', value: summary.total, color: '#111827' },
    { label: '보충 예정', value: summary.scheduled, color: '#334155' },
    { label: '보충 완료', value: summary.completed, color: '#166534' },
    { label: '보충 미정', value: summary.unscheduled, color: '#9a3412' },
    { label: '보충 불필요', value: summary.notNeeded, color: '#64748b' },
  ]
    .map(
      (s) => `<div class="stat">
        <div class="stat-label">${escapeHtml(s.label)}</div>
        <div class="stat-value" style="color:${s.color}">${s.value}</div>
      </div>`
    )
    .join('');

  const bodyRows = sorted.length
    ? sorted
        .map((r) => {
          const badge = STATUS_BADGE[r.status];
          const makeupInfo = [
            r.makeupDate ? `보충 ${shortDate(r.makeupDate)}` : '',
            r.makeupTime ? r.makeupTime : '',
          ]
            .filter(Boolean)
            .join(' ');
          const doneInfo = r.completedDate ? `완료 ${shortDate(r.completedDate)}` : '';
          return `<tr>
            <td class="date">${r.date} (${weekdayLabel(r.date)})</td>
            <td class="name">${escapeHtml(r.studentName)}</td>
            <td class="grade">${r.grade ? escapeHtml(r.grade) : '-'}</td>
            <td class="class">${r.className ? escapeHtml(r.className) : '-'}</td>
            <td class="reason">${escapeHtml(r.reason)}</td>
            <td class="status">
              <span class="badge" style="background:${badge.bg};color:${badge.color}">${escapeHtml(
                r.statusText
              )}</span>
              ${makeupInfo ? `<div class="sub">${escapeHtml(makeupInfo)}</div>` : ''}
              ${doneInfo ? `<div class="sub">${escapeHtml(doneInfo)}</div>` : ''}
            </td>
          </tr>`;
        })
        .join('')
    : `<tr><td class="empty" colspan="6">해당 조건의 결석·보충 기록이 없습니다.</td></tr>`;

  const html = `<!doctype html>
<html lang="ko">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  * { box-sizing: border-box; }
  body { margin: 0; padding: 16px; font-family: 'Malgun Gothic', 'Apple SD Gothic Neo', sans-serif; color: #111; }
  .head { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 10px; }
  h1 { font-size: 20px; margin: 0; }
  .sub { font-size: 12px; color: #666; margin-top: 4px; }
  .date { font-size: 11px; color: #888; }
  .target { margin: 10px 0 12px; font-size: 13px; }
  .target strong { font-size: 15px; }
  .stats { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
  .stat { flex: 1 1 0; min-width: 96px; border: 1px solid #e2e2e2; border-radius: 6px; padding: 8px 10px; }
  .stat-label { font-size: 11px; color: #666; }
  .stat-value { font-size: 20px; font-weight: 700; margin-top: 2px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #d4d4d4; padding: 6px 7px; font-size: 12px; text-align: left; vertical-align: top; }
  th { background: #f3f4f6; font-size: 12px; text-align: center; }
  td.date { white-space: nowrap; }
  td.name { font-weight: 700; white-space: nowrap; }
  td.grade, td.class { white-space: nowrap; }
  td.status { white-space: nowrap; }
  .badge { display: inline-block; padding: 2px 7px; border-radius: 999px; font-size: 11px; font-weight: 600; }
  .sub { font-size: 10px; color: #777; margin-top: 3px; }
  .empty { text-align: center; color: #999; padding: 24px 0; }
</style>
</head>
<body>
  <div class="head">
    <div>
      <h1>${escapeHtml(title)}</h1>
      ${subtitle ? `<div class="sub">${escapeHtml(subtitle)}</div>` : ''}
    </div>
    <div class="date">출력일 ${new Date().toLocaleDateString('ko-KR')}</div>
  </div>
  <div class="target">${escapeHtml(monthLabel)} · <strong>${escapeHtml(teacherName)}</strong></div>
  <div class="stats">${summaryCells}</div>
  <table>
    <thead>
      <tr>
        <th>결석일</th>
        <th>학생</th>
        <th>학년</th>
        <th>수업</th>
        <th>사유</th>
        <th>보충 상태</th>
      </tr>
    </thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body>
</html>`;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.visibility = 'hidden';
  document.body.appendChild(iframe);

  const removeIframe = () => {
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 1500);
  };

  const runPrint = () => {
    const win = iframe.contentWindow;
    if (!win) {
      removeIframe();
      return;
    }
    try {
      win.focus();
      win.print();
    } catch (err) {
      console.error(err);
    }
    removeIframe();
  };

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    removeIframe();
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  // 내용이 그려진 뒤 인쇄되도록 살짝 기다려요.
  setTimeout(runPrint, 400);
}